import { useEffect, useState } from "react";
import {
  DeleteButton,
  Edit,
  ReferenceInput,
  required,
  SaveButton,
  SelectInput,
  SimpleForm,
  TextInput,
  Toolbar,
  useDataProvider,
  useGetList,
  useNotify,
  useRecordContext,
  useRedirect,
  useRefresh,
} from "react-admin";
import { useTheme } from '@mui/material/styles';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { CustomActions } from '../../components';
import DeleteIcon from '@mui/icons-material/Delete';
import _ from 'lodash';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const CustomToolbar = ({ changedList, originList }) => {
  const record = useRecordContext();
  const redirect = useRedirect(); // Hook to redirect to another page
  const theme = useTheme(); // Access the theme
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const backgroundColor = theme.palette.background.paper; // Default background color

  const addedItems = _.differenceBy(changedList, originList, "id");
  const deletedItems = _.differenceBy(originList, changedList, "id");
  const changedOrderItems = _.filter(changedList, (newItem) => {
    const originalItem = _.find(originList, { id: newItem?.id });
    return originalItem && originalItem.order !== newItem.order;
  });
  const isDifferent = !_.isEqual(changedList, originList);
  const isDeletable = record?.schedules?.items?.length === 0;

  const handleCancel = () => {
    redirect('list', 'routes');
  };

  const handleUpdate = async (res) => {
    console.log(res)
    try {
      if (addedItems.length > 0) {
        addedItems.forEach(async (item) => {
          const data = {
            checkpoint_id: item.id,
            order: item.order,
            route_id: record.id,
          };
          await dataProvider.create("routeCheckPointss", { data });
        });
      }
      if (deletedItems.length > 0) {
        await dataProvider.deleteMany("routeCheckPointss", { ids: deletedItems.map((item) => item.rcpId) })
      }
      if (changedOrderItems.length > 0) {
        await Promise.all(
          changedOrderItems.map((item) =>
            dataProvider.update("routeCheckPointss", {
              id: item.rcpId,
              data: {
                id: item.rcpId,
                order: item.order,
              },
            })
          )
        )
      }

      const id = res.id;
      const data = {
        id: res.id,
        name: res.name,
        route_type_id: res.route_type_id,
        site_id: res.site_id,
      };
      await dataProvider.update("routes", { id, data });

      notify('Route updated successfully', { type: 'success' });
      refresh();
      redirect('list', 'routes');
    } catch (error) {
      console.error('Error updating Route:', error);
      notify('Error updating Route', { type: 'error' });
    }
  };

  const handleSuccessDelete = async (res) => {
    try {
      const { route_checkpoints, id } = res;
      if (route_checkpoints.items.length > 0) {
        route_checkpoints.items.forEach(async (item) => {
          const id = item.id;
          const previousData = item;
          await dataProvider.delete("routeCheckPoints", { id, previousData });
        });
      }

      const previousData = res;
      await dataProvider.delete("routes", { id, previousData });
      notify('Route deleted successfully', { type: 'success' });
      redirect('list', 'routes');
    } catch (error) {
      console.error('Error deleting Route:', error);
      notify('Error deleting Route', { type: 'error' });
    }
  };

  return (
    <Toolbar
      sx={{
        backgroundColor, // Set the background color
      }}
    >
      <Box display="flex" justifyContent="space-between" width="100%">
        <Box display='flex' alignItems='center'>
          <DeleteButton
            type='button'
            size='small'
            label='Delete'
            sx={{ textTransform: 'none', minWidth: '95px', minHeight: '30.75px' }}
            mutationOptions={{ onSuccess: handleSuccessDelete }}
            disabled={!isDeletable}
          />
          {!isDeletable && (
            <Tooltip
              title='This route is referenced by other schedules.'
              placement='top'
            >
              <InfoOutlinedIcon sx={{ fontSize: 15 }} />
            </Tooltip>
          )}
        </Box>
        <Box>
          <Button
            variant="text"
            onClick={handleCancel}
            sx={{
              color: theme.palette.common.white, // Optional: primary color for cancel button text
              borderColor: theme.palette.primary.main, // Optional: primary color for cancel button border
              marginRight: theme.spacing(1),
              textTransform: 'none',
              minWidth: '95px',
            }}
            size='small'
          >
            Cancel
          </Button>
          <SaveButton
            alwaysEnable={isDifferent}
            type='button'
            label='Update'
            size='small'
            icon={null}
            sx={{ textTransform: 'none', minWidth: '95px' }}
            mutationOptions={{ onSuccess: handleUpdate }}
          />
        </Box>
      </Box>
    </Toolbar>
  );
};

const CheckpointList = ({ onChange, onSetOriginal }) => {
  const record = useRecordContext();
  // Fetch all checkpoints
  const { data: originalCheckpoints = [], isLoading } = useGetList('route_checkpoints', { filter: { route_id: record.id }, pagination: { page: 1, perPage: 1000 } });
  const { data: siteCheckpoints = [], isLoading: isSiteCheckpointsLoading } = useGetList('checkpoints', { filter: { site_id: record.site_id }, pagination: { page: 1, perPage: 1000 } });

  const [routeCheckpoints, setRouteCheckpoints] = useState([...originalCheckpoints]);

  useEffect(() => {
    // transform route checkpoints format to match the checkpoints format
    const transformedCheckpoints = originalCheckpoints.map((rcp) => ({
      id: rcp.checkpoint_id,
      name: rcp.checkpoint.name,
      order: rcp.order,
      rcpId: rcp.id,
    })).sort((a, b) => a.order - b.order);

    setRouteCheckpoints(prev => {
      // prevent loop: only update if different
      const same = JSON.stringify(prev) === JSON.stringify(transformedCheckpoints);
      if (same) return prev;
      return transformedCheckpoints;
    });

    // set original checkpoints
    onSetOriginal(prev => {
      // prevent loop: only update if different
      const same = JSON.stringify(prev) === JSON.stringify(transformedCheckpoints);
      if (same) return prev;
      return transformedCheckpoints;
    });

  }, [originalCheckpoints]);

  // Calculate available checkpoints (site checkpoints not already in route)
  const availableCheckpoints = siteCheckpoints.filter(siteCheckpoint =>
    !routeCheckpoints.some(routeCheckpoint => routeCheckpoint.id === siteCheckpoint.id)
  );

  useEffect(() => {
    onChange(routeCheckpoints);
  }, [routeCheckpoints]);

  const handleAddCheckpoint = (selectedId) => {
    if (selectedId) {
      const selectedCheckpoint = siteCheckpoints.find((cp) => cp.id === selectedId);

      const updatedRouteCheckpoints = [
        ...routeCheckpoints,
        {
          id: selectedCheckpoint.id,
          name: selectedCheckpoint.name,
          order: routeCheckpoints.length
        }
      ];

      setRouteCheckpoints(updatedRouteCheckpoints);
    }
  };

  const handleDelete = (rcp) => {
    const updatedrcps = routeCheckpoints.filter((item) => item.id !== rcp.id);
    setRouteCheckpoints(updatedrcps.map((urcps, idx) => ({ ...urcps, order: idx })));
  };

  const moveUp = (rcp) => {
    if (rcp.order === 0) return;
    swapOrder(rcp, "up");
  };

  const moveDown = (rcp) => {
    if (rcp.order === routeCheckpoints.length - 1) return;
    swapOrder(rcp, "down");
  };

  const swapOrder = (rcp, orderStatus) => {
    if (orderStatus === "up") {
      const targetRcp = routeCheckpoints.find((item) => item.order === rcp.order - 1);
      setRouteCheckpoints([
        ...routeCheckpoints.filter((rcpItem) => rcpItem.id !== targetRcp.id && rcpItem.id !== rcp.id),
        { ...rcp, order: rcp.order - 1 },
        { ...targetRcp, order: targetRcp.order + 1 },
      ].sort((a, b) => a.order - b.order));
    } else if (orderStatus === "down") {
      const targetRcp = routeCheckpoints.find((item) => item.order === rcp.order + 1);
      setRouteCheckpoints([
        ...routeCheckpoints.filter((rcpItem) => rcpItem.id !== targetRcp.id && rcpItem.id !== rcp.id),
        { ...rcp, order: rcp.order + 1 },
        { ...targetRcp, order: targetRcp.order - 1 },
      ].sort((a, b) => a.order - b.order));
    }
  };

  if (isLoading && availableCheckpoints.length === 0) return <Typography variant="caption">Loading...</Typography>
  else {

    return (
      // scrollable div
      <>
        <Typography variant="subtitle2" gutterBottom>
          Checkpoint Selection
        </Typography>
        <div style={{ width: '100%', overflowY: 'auto', maxHeight: '200px' }} >
          {routeCheckpoints.length > 0 && routeCheckpoints.map((rcp, index, array) => (
            <div
              key={index}
              style={{
                display: 'flex',
                width: '100%',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px',
                backgroundColor: 'rgb(30, 30, 46)',
                border: '1px solid rgb(120, 120, 120)',
                marginTop: '10px',
              }}
            >
              <div>{index + 1}</div>
              <div
                style={{
                  margin: 'auto',
                  marginLeft: '10px'
                }}
              >{rcp?.name}</div>
              <IconButton disabled={index === 0} onClick={() => moveUp(rcp)}>
                <KeyboardArrowUpIcon />
              </IconButton>
              <IconButton disabled={index === array.length - 1} onClick={() => moveDown(rcp)}>
                <KeyboardArrowDownIcon />
              </IconButton>
              <IconButton onClick={() => handleDelete(rcp)}>
                <DeleteIcon />
              </IconButton>
            </div>
          ))}
          {!isSiteCheckpointsLoading && availableCheckpoints.length > 0 && (
            <FormControl fullWidth>
              <InputLabel id="select-checkpoint">+ Add checkpoint</InputLabel>
              <Select
                labelId="select-checkpoint"
                value={''}
                onChange={(e) => handleAddCheckpoint(e.target.value)}
                MenuProps={{
                  PaperProps: {
                    style: {
                      transform: 'translate3d(0, 0, 0)'
                    }
                  }
                }}
              >
                {availableCheckpoints.map((cp, idx) => <MenuItem value={cp?.id} key={idx}>{cp?.name}</MenuItem>)}
              </Select>
            </FormControl>
          )}
        </div>
      </>
    );
  }
};

const EditRoute = () => {
  const [changedList, setChangedList] = useState([]);
  const [originList, setOriginList] = useState([]);

  return (
    <Card variant='elevation' sx={{ width: '50%' }}>
      <CardContent>
        <Edit actions={<CustomActions title="Edit Route" type='edit' />}>
          <SimpleForm toolbar={<CustomToolbar changedList={changedList} originList={originList} />} >
            <Typography variant="subtitle2">
              Details
            </Typography>
            <TextInput source="name" label='Name' fullWidth validate={[required('Name is required')]} />
            <ReferenceInput source="site_id" reference="sites" label="Site">
              <SelectInput optionText="name" fullWidth validate={[required('Site selection is required')]} />
            </ReferenceInput>
            <ReferenceInput source="route_type_id" reference="routeTypes" label="Route Type">
              <SelectInput optionText="name" fullWidth validate={[required('Route type selection is required')]} />
            </ReferenceInput>
            <CheckpointList onChange={setChangedList} onSetOriginal={setOriginList} />
          </SimpleForm>
        </Edit>
      </CardContent>
    </Card>
  )
}

export default EditRoute;
export { EditRoute };